// Modern JSX transform - React import not needed
import { memo } from 'react'
import { trackSpotModeCancel } from '../../utils/analytics'

/**
 * SpotModeBanner - Top floating banner shown during spot mode
 * Displays instructions and cancel button
 * 
 * @component
 * @example
 * <SpotModeBanner
 *   t={t}
 *   spotModeLocation={{lat: 40.7, lng: -74.0}}
 *   spotModeEntity={{name: "Food Truck"}}
 *   spotModeNotes="notes text"
 *   spotModeImages={[file1, file2]}
 *   spotModeStartTime={timestamp}
 *   onCancel={() => deactivateSpotMode()}
 * />
 */
const SpotModeBanner = memo(function SpotModeBanner({
  t,
  spotModeLocation,
  spotModeEntity,
  spotModeNotes = '',
  spotModeImages = [],
  spotModeStartTime = null,
  onCancel,
  pendingAuthSubmit = false,
  variant = 'spot',   // 'spot' | 'listing'
}) {
  const handleCancel = () => {
    // Determine cancellation stage
    let stage = 'no_location'
    if (spotModeLocation && !spotModeEntity) {
      stage = 'location_only'
    } else if (spotModeEntity && spotModeImages.length === 0) {
      stage = 'entity_selected'
    } else if (spotModeImages.length > 0) {
      stage = 'images_added'
    }
    
    // Calculate duration
    const duration = spotModeStartTime ? Math.floor((Date.now() - spotModeStartTime) / 1000) : 0
    
    // Track cancellation
    trackSpotModeCancel(
      !!spotModeLocation,
      !!spotModeEntity,
      !!spotModeNotes,
      spotModeImages.length,
      duration,
      stage
    )
    
    onCancel()
  }

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[1000] max-w-2xl w-full px-4">
      <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl px-4 py-3 flex flex-col gap-1.5 border border-primary/20">
        {/* Row 1: Step pills + cancel button */}
        <div className="flex items-center gap-3">
        {/* Step progress pills */}
        <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
          {variant === 'listing' ? (
            <>
              {/* Listing: 2-step flow */}
              <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors ${!spotModeLocation ? 'bg-primary text-white' : 'bg-primary/10 text-primary line-through opacity-60'}`}>
                <span className={`w-4 h-4 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${!spotModeLocation ? 'bg-white/30 text-white' : 'bg-primary/20 text-primary'}`}>1</span>
                {t('listings.stepPickup', 'Pickup spot')}
              </div>
              <div className="w-3 h-px bg-gray-300 flex-shrink-0" />
              <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors ${spotModeLocation ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
                <span className={`w-4 h-4 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${spotModeLocation ? 'bg-white/30 text-white' : 'bg-gray-200 text-gray-500'}`}>2</span>
                {t('listings.stepDetails', 'Details')}
              </div>
            </>
          ) : (
            <>
              {/* Spot: 3-step flow */}
              <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors ${!spotModeLocation ? 'bg-primary text-white' : 'bg-primary/10 text-primary line-through opacity-60'}`}>
                <span className={`w-4 h-4 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${!spotModeLocation ? 'bg-white/30 text-white' : 'bg-primary/20 text-primary'}`}>1</span>
                {t('map.spotMode.stepSetLocation', 'Set Location')}
              </div>
              <div className="w-3 h-px bg-gray-300 flex-shrink-0" />
              <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors ${spotModeLocation && !spotModeEntity ? 'bg-primary text-white' : spotModeEntity ? 'bg-primary/10 text-primary line-through opacity-60' : 'bg-gray-100 text-gray-400'}`}>
                <span className={`w-4 h-4 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${spotModeLocation && !spotModeEntity ? 'bg-white/30 text-white' : 'bg-gray-200 text-gray-500'}`}>2</span>
                {t('map.spotMode.stepChooseVendor', 'Choose Vendor')}
              </div>
              <div className="w-3 h-px bg-gray-300 flex-shrink-0" />
              <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors ${spotModeEntity ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
                <span className={`w-4 h-4 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${spotModeEntity ? 'bg-white/30 text-white' : 'bg-gray-200 text-gray-500'}`}>3</span>
                {t('map.spotMode.stepReadyToSubmit', 'Ready!')}
              </div>
            </>
          )}
        </div>

        {/* Cancel button */}
        <button
          onClick={handleCancel}
          className="flex-shrink-0 text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-colors"
          aria-label={t('common.cancel', 'Cancel')}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        </div>

        {/* Row 2: Contextual hint */}
        {pendingAuthSubmit ? (
          <p className="text-xs text-center text-amber-600 font-medium">
            {t('spotMode.draftSaved', 'Draft saved — sign in to finish')}
          </p>
        ) : !spotModeLocation ? (
          <p className="text-xs text-center text-primary font-medium animate-pulse">
            {variant === 'listing'
              ? t('listings.dropPinHint', 'Tap the map to set the pickup / exchange spot')
              : t('map.spotMode.clickToSetLocation', 'Tap anywhere on the map to drop a pin')}
          </p>
        ) : null}
      </div>
    </div>
  )
})

export default SpotModeBanner
